/**
 * ModuleConflictBanner.tsx — shows hard/soft conflict warnings for a single module
 *
 * Used at the top of each module's dedicated page. Reads block + soft-warning
 * info from the registry hook and renders the appropriate banner. If a hard
 * conflict is active, the page's enable toggle should also be disabled.
 */

import { Ban, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleInfoModal from '@/components/ModuleInfoModal';

interface Props {
  /** Module ID this page represents. */
  moduleId: string;
}

export default function ModuleConflictBanner({ moduleId }: Props) {
  const { blockInfo, softWarnings, isEnabled } = useModulesEnabled();
  const enabled = isEnabled(moduleId);
  const block = !enabled ? blockInfo(moduleId) : null;
  const warnings = enabled ? softWarnings(moduleId) : [];

  if (!block && warnings.length === 0) {
    // Show a neutral "no conflicts" indicator plus the info button so the user knows the system checked
    return (
      <div className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-xs flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-emerald-300">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>No conflicts detected with currently-enabled modules.</span>
        </div>
        <ModuleInfoModal moduleId={moduleId} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <ModuleInfoModal moduleId={moduleId} />
      </div>
      {block && (
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-3">
          <div className="flex items-center gap-2 text-sm font-bold text-red-200 mb-1.5">
            <Ban className="w-4 h-4" />
            Cannot be enabled — hard conflict with "{block.blockedBy}"
          </div>
          <div className="text-xs text-red-100/80 leading-relaxed">{block.reason}</div>
          <div className="text-[11px] text-red-300/70 mt-2 italic">
            Disable the conflicting module on the Modules Hub before enabling this one.
          </div>
        </div>
      )}
      {warnings.map((w, i) => (
        <div key={i} className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-3">
          <div className="flex items-center gap-2 text-sm font-bold text-amber-200 mb-1.5">
            <AlertTriangle className="w-4 h-4" />
            Soft conflict with "{w.withLabel}"
          </div>
          <div className="text-xs text-amber-100/80 leading-relaxed">{w.reason}</div>
        </div>
      ))}
    </div>
  );
}
